type RefCallback = (element: HTMLElement | null) => (() => void) | undefined;

export type MinHeightSync = {
  /** Ref for the element whose height the targets follow. */
  source: RefCallback;
  /** Ref for every element that must be at least as tall as the source. */
  target: RefCallback;
};

// Keeps every target element at least as tall as the source element. The
// height goes straight from a ResizeObserver on the source to the targets'
// inline style. Routing it through React state leaves a render pending after
// every commit, and a list with enough rows then hits React's nested-update
// limit ("Maximum update depth exceeded").
//
// Both refs return a cleanup function, which React 19 calls instead of
// invoking the ref with null.
export const createMinHeightSync = (): MinHeightSync => {
  const targets = new Set<HTMLElement>();
  let height = 0;

  const apply = (element: HTMLElement) => {
    element.style.minHeight = height ? `${height}px` : '';
  };

  const publish = (measured: number) => {
    // Sub-pixel jitter while the layout settles must not flip the height
    // between two integers, so anything under a full pixel keeps the current
    // value. Heights round up so a target is never under-sized.
    if (Math.abs(measured - height) < 1) {
      return;
    }
    height = Math.ceil(measured);
    targets.forEach(apply);
  };

  return {
    source: (element) => {
      if (!element) {
        return undefined;
      }
      const observer = new ResizeObserver((entries) => {
        entries.forEach((entry) => publish(entry.contentRect.height));
      });
      observer.observe(element);
      return () => observer.disconnect();
    },
    target: (element) => {
      if (!element) {
        return undefined;
      }
      targets.add(element);
      apply(element);
      return () => {
        targets.delete(element);
      };
    },
  };
};
